import { dehydrate, HydrationBoundary } from "@tanstack/react-query";
import { ContentPreview } from "@turtlewave/types";
import { useMemo } from "react";
import { type LoaderFunctionArgs, type MetaFunction } from "react-router";

import { CompilationsList } from "../compilations/components/CompilationsList";
import { useCompilationList } from "../compilations/hooks/useCompilationList";
import { compilationListQuery } from "../compilations/queries";
import { SectionHeader } from "../components/SectionHeader";
import { ContentErrorBoundary } from "../content/components/ContentErrorBoundary";
import { ContentList } from "../content/components/ContentList";
import { useContentList } from "../content/hooks/useContentList";
import { contentListQuery } from "../content/queries";
import { useDehydratedState } from "../hooks/useDehydratedState";
import { getBrowserQueryClient } from "../providers/queryClient";
import { Loadable } from "../types/loadable";
import { apiUnreachableInDev, loaderQueryClient } from "../utils/loaders";
import { canonical, pageTitle } from "../utils/meta";

export async function loader({ context }: LoaderFunctionArgs) {
  const queryClient = loaderQueryClient(context);
  await Promise.all([
    queryClient.ensureQueryData(contentListQuery()),
    queryClient.ensureQueryData(compilationListQuery()),
  ]).catch((error: unknown) => {
    if (!apiUnreachableInDev(error)) throw error;
  });
  return { dehydratedState: dehydrate(queryClient) };
}

// Starts the fetches and returns; the page renders now, its hooks fill it in.
export function clientLoader() {
  const client = getBrowserQueryClient();
  void client.prefetchQuery(contentListQuery());
  void client.prefetchQuery(compilationListQuery());
  return { dehydratedState: null };
}

export const meta: MetaFunction = () => [
  { title: pageTitle("Gallery") },
  canonical("/gallery"),
];

function getDateValue(content: ContentPreview): number {
  const { year, month, day } = content.publish_date;
  return (year ?? 0) * 10000 + (month ?? 0) * 100 + (day ?? 0);
}

export function Gallery() {
  const compilationsQuery = useCompilationList();
  const contentQuery = useContentList();

  const sortedContent = useMemo(() => {
    if (!contentQuery.data) return undefined;
    return [...contentQuery.data].sort((a, b) => {
      const dateDiff = getDateValue(b) - getDateValue(a);
      return dateDiff !== 0 ? dateDiff : a.title.localeCompare(b.title);
    });
  }, [contentQuery.data]);

  const sortedContentQuery: Loadable<ContentPreview[]> = {
    data: sortedContent,
    isLoading: contentQuery.isLoading,
    error: contentQuery.error,
  };

  return (
    <div className="w-full px-6 py-12 md:px-12 lg:mt-15 lg:ml-8">
      {/* Compilations Section */}
      <section aria-label="List of Compilations" className="mb-16">
        <SectionHeader title="COMPILATIONS" className="mb-4" />

        <ContentErrorBoundary>
          <CompilationsList query={compilationsQuery} variant="carousel" />
        </ContentErrorBoundary>
      </section>

      {/* Content Section */}
      <section aria-label="List of All Artworks">
        <SectionHeader title="ALL WORKS" className="mb-4" />

        <ContentErrorBoundary>
          <ContentList query={sortedContentQuery} />
        </ContentErrorBoundary>
      </section>
    </div>
  );
}

export default function GalleryRoute() {
  const dehydratedState = useDehydratedState();
  return (
    <HydrationBoundary state={dehydratedState}>
      <Gallery />
    </HydrationBoundary>
  );
}
